/**
 * JS-1396 — framework-free helpers for the WebAuthn ceremonies.
 *
 * Kept out of the Angular service so they can be unit-tested from tests/
 * without pulling in the framework.
 */

export function base64UrlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const withPadding = padded + '=='.substring(0, (4 - (padded.length % 4)) % 4);
  const binary = atob(withPadding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function isPasskeySupported(): boolean {
  return typeof window !== 'undefined'
    && typeof (window as any).PublicKeyCredential !== 'undefined'
    && !!(navigator.credentials && navigator.credentials.create);
}

/** Default label for a newly enrolled credential, shown in Security settings. */
export function describeThisDevice(userAgent: string): string {
  const platform =
    /iPhone/.test(userAgent) ? 'iPhone' :
    /iPad/.test(userAgent) ? 'iPad' :
    /Macintosh/.test(userAgent) ? 'Mac' :
    /Windows/.test(userAgent) ? 'Windows' :
    /Android/.test(userAgent) ? 'Android' : null;

  const browser =
    /Edg\//.test(userAgent) ? 'Edge' :
    /Chrome\//.test(userAgent) ? 'Chrome' :
    /Firefox\//.test(userAgent) ? 'Firefox' :
    /Safari\//.test(userAgent) ? 'Safari' : null;

  if (!platform || !browser) {
    return 'Passkey';
  }
  return `${platform} — ${browser}`;
}

/** Visible and focused: Chrome refuses navigator.credentials.get() otherwise. */
function isPageReady(): boolean {
  if (typeof document === 'undefined') {
    return true;
  }
  const focused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
  return document.visibilityState === 'visible' && focused;
}

/**
 * JS-1396 automatic passkey prompt: run `start` once the page is visible AND
 * focused - now, or at the first window `focus` / `visibilitychange` that
 * makes both true. A background tab must not raise an OS dialog, and Chrome
 * refuses the ceremony in a visible but unfocused window (another app in
 * front), which would waste the one automatic attempt. Returns a cancel
 * function (removes the listeners; a no-op once `start` has run).
 */
export function whenPageReady(start: () => void): () => void {
  if (isPageReady()) {
    start();
    return () => {};
  }
  const stop = () => {
    window.removeEventListener('focus', listener);
    document.removeEventListener('visibilitychange', listener);
  };
  const listener = () => {
    if (isPageReady()) {
      stop();
      start();
    }
  };
  window.addEventListener('focus', listener);
  document.addEventListener('visibilitychange', listener);
  return stop;
}

/** An AbortController where the browser has one (all passkey browsers do). */
export function newAbortController(): AbortController | null {
  return typeof AbortController === 'function' ? new AbortController() : null;
}

/**
 * A passkey ceremony that ended without the user getting through it: a
 * cancelled or timed-out sheet, no passkey on this device, or a browser that
 * refuses a prompt nobody clicked for (Safari) - all NotAllowedError - and an
 * aborted (AbortError) or refused (SecurityError) one. After an automatic
 * prompt these are not worth a toast: the screen with its button is the answer.
 */
export function isSilentCeremonyFailure(error: any): boolean {
  const name = error && error.name;
  return name === 'NotAllowedError' || name === 'AbortError' || name === 'SecurityError';
}

/**
 * Resolves once the language the translate pipe will render in has loaded,
 * or after `timeoutMs` at the latest. A hard page load fetches the default
 * language (de) and the account language in parallel, so a screen painted at
 * once shows empty strings, then German, then the account language. Takes the
 * TranslateService duck-typed so this file stays framework-free.
 */
export function whenTranslationsLoaded(translate: any, timeoutMs = 3000): Promise<void> {
  const lang = translate.currentLang || translate.defaultLang;
  const loaded = () => !lang || !!(translate.translations && translate.translations[lang]);
  if (loaded()) {
    return Promise.resolve();
  }
  return new Promise<void>(resolve => {
    const subscriptions: any[] = [];
    let timer: any = null;
    const finish = () => {
      subscriptions.forEach(subscription => subscription.unsubscribe());
      clearTimeout(timer);
      resolve();
    };
    const check = () => {
      if (loaded()) {
        finish();
      }
    };
    timer = setTimeout(finish, timeoutMs);
    [translate.onLangChange, translate.onDefaultLangChange, translate.onTranslationChange]
      .filter(emitter => emitter && typeof emitter.subscribe === 'function')
      .forEach(emitter => subscriptions.push(emitter.subscribe(check)));
  });
}
